/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/EndVerifyScreen.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [9] end_verify — màn xác nhận chấm công ra
 *          riêng: trễ >15 phút so giờ kết ca ⇒ lý do BẮT BUỘC; đúng giờ ⇒ ghi
 *          chú KHÔNG bắt buộc. Web trước đây chỉ có 1 nút "Chấm công ra" gọi
 *          thẳng API, không có khái niệm "trễ giờ RA" hay màn xác nhận nào.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [9]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx renderEndVerify (:1088-1138)
 * - Timing calc: frontend/src/utils/attendanceTiming.ts computeClockOutTimingWarning
 *
 * 2026-09-04 (structural layout parity, root causes #1/#2): removed the
 * `.card` wrapper (mobile's renderEndVerify is a bare fragment) and the
 * duplicate in-body title+back header (was added 2026-09-04 as a stopgap
 * before AttendanceModern.tsx's page header became state-driven — that
 * header now supplies "Xác thực chấm công ra" + wires its back button to
 * 'checklist'/'home' for this screen, headerConfig.end_verify, mirroring
 * mobile's own headerConfig.end_verify.onBack → 'checklist'). Also added the
 * conditional warning-Banner wrapper around the reason textarea when late,
 * matching mobile's `lateInfo.isLate ? <Banner variant="warn"><Textarea .../>
 * </Banner> : <Textarea .../>` branch (AttendanceScreen.tsx:1134-1151) — web
 * previously never wrapped the textarea regardless of late/on-time.
 *  */

import React, { useState } from 'react';
import styles from '@/pages/AttendanceModern.module.css';

interface EndVerifyScreenProps {
  geofenceResult: { distance: number; within: boolean } | null;
  isLate: boolean;
  lateMinutes: number;
  loading: boolean;
  errorMessage: string | null;
  onConfirm: (notes: string | undefined) => void;
}

export const EndVerifyScreen: React.FC<EndVerifyScreenProps> = ({
  geofenceResult,
  isLate,
  lateMinutes,
  loading,
  errorMessage,
  onConfirm,
}) => {
  const [text, setText] = useState('');
  const canConfirm = !isLate || text.trim().length > 0;

  const reasonField = (
    <div className={styles.overrideRow} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <label htmlFor="end-verify-note" className={styles.photoLabel}>
        {isLate ? 'Lý do *' : 'Ghi chú (không bắt buộc)'}
      </label>
      <textarea
        id="end-verify-note"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 500))}
        rows={3}
        maxLength={500}
        data-testid="end-verify-reason"
      />
      {isLate && !text.trim() && (
        <p className={styles.accuracyNote}>Bắt buộc nhập lý do trước khi xác nhận chấm công ra.</p>
      )}
    </div>
  );

  return (
    <>
      <p className={styles.cardDescription}>{isLate ? `Trễ ${lateMinutes} phút` : 'Ra đúng giờ'}</p>

      {geofenceResult && (
        <p
          className={`${styles.accuracyMessage} ${geofenceResult.within ? styles.accuracyValid : styles.accuracyInvalid}`}
          data-testid="end-verify-distance-badge"
        >
          {geofenceResult.within ? '✓' : '✗'} {Math.round(geofenceResult.distance)}m
        </p>
      )}

      {/* mobile only wraps the reason field in a warning Banner when late —
          on-time keeps the bare Textarea (AttendanceScreen.tsx:1134-1151). */}
      {isLate ? (
        <div className={styles.timingWarningBanner} data-testid="end-verify-late-reason-banner">
          {reasonField}
        </div>
      ) : (
        reasonField
      )}

      {errorMessage && (
        <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert">
          {errorMessage}
        </p>
      )}

      <div className={styles.actionsRow}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => onConfirm(text.trim() || undefined)}
          disabled={loading || !canConfirm}
          data-testid="end-verify-confirm"
        >
          Xác nhận chấm công ra
        </button>
      </div>
    </>
  );
};

export default EndVerifyScreen;
